import { useTranslation } from "react-i18next";
import {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Label from "@components/Label";
import Button from "@components/Button";
import Alert from "@components/Alert";
import useDataStore from "@state/DataStore";
import Input from "@components/Input";
import useLoading from "src/services/LoadingService";
import useConfiguration from "./use-configuration-hook";
import useSaveConfiguration from "./use-save-configuration-hook";
import { ConfigurationType } from "./types";

type Option = {
  label: string;
  value: number;
};

const START_YEAR = 2023;

const getOptions = (): Option[] => {
  const currentYear = new Date().getFullYear();
  const years: Option[] = [];
  for (let i = START_YEAR; i <= currentYear + 1; i += 1) {
    years.push({ label: i.toString(), value: i });
  }
  return years;
};

const ConfigurationsPage = () => {
  const { t } = useTranslation();
  const [configuration, setConfiguration] =
    useState<ConfigurationType | null>();
  const options = useMemo(() => getOptions(), []);
  const [message, setMessage] = useState<string | null>(null);

  const {
    fetchConfiguration,
    loading: configurationLoading,
    error: configurationError,
  } = useConfiguration();

  useLoading(configurationLoading);

  useEffect(() => {
    (async () => {
      const data = await fetchConfiguration();
      setConfiguration(data);
    })();
  }, [fetchConfiguration]);

  const {
    saveConfiguration,
    loading: saveLoading,
    error: saveError,
  } = useSaveConfiguration();

  const saveHandler = useCallback(async () => {
    if (configuration) {
      const result = await saveConfiguration(configuration);
      if (result) {
        setMessage(t("Saved"));
        useDataStore.getState().setConfiguration(configuration);
      }
    }
  }, [saveConfiguration, t, configuration]);

  const onActiveSeasonChangeHandler: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event) => {
      setConfiguration((c) => {
        const config = c ?? ({} as ConfigurationType);
        return {
          ...config,
          activeSeason: parseInt(event.target.value, 10),
        };
      });
    }, []);

  const onDiscountChangeHanlder = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setConfiguration((c) => {
        const config = c ?? ({} as ConfigurationType);
        return {
          ...config,
          discount: event.target.value ? parseInt(event.target.value, 10) : 0,
        };
      });
    },
    []
  );

  const onDiscountEndChangeHanlder = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setConfiguration((c) => {
        const config = c ?? ({} as ConfigurationType);
        return {
          ...config,
          discountEnd: event.target.value,
        };
      });
    },
    []
  );

  return (
    <>
      {message && (
        <Alert
          onClose={() => {
            setMessage(null);
          }}
        >
          {message}
        </Alert>
      )}
      {configurationError && <Alert type="error">{configurationError}</Alert>}
      {saveError && <Alert type="error">{saveError}</Alert>}
      <div className="mb-6">
        <Label name="activeSeason">{t<string>("Active season")}</Label>
        <select
          id="activeSeason"
          onChange={onActiveSeasonChangeHandler}
          value={configuration?.activeSeason}
          className="!max-w-[200px] !bg-gray-50 border !border-gray-300 !text-gray-900 !text-sm !rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full !p-2.5 mb-2"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <Input
        type="number"
        name="discount"
        label={`${t<string>("Discount")} (%)`}
        value={configuration?.discount?.toString() ?? "0"}
        min={0}
        max={100}
        step={10}
        onChange={onDiscountChangeHanlder}
      />
      <Input
        type="date"
        name="discountEnd"
        label={`${t<string>("Discount end")}`}
        value={configuration?.discountEnd ?? ""}
        onChange={onDiscountEndChangeHanlder}
      />
      <Button
        onClick={saveHandler}
        loading={saveLoading}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        }
      >
        {t("Confirm")}
      </Button>
    </>
  );
};

export default ConfigurationsPage;
